import * as React from 'react'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { FaIcon, faClose } from '@/lib/fa'
import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  useMutationCreateCompanyOnServer,
  useQueryWpfCompaniesOnServer,
  WpfCompanyModel,
} from '@/lib/api/server/serverHooks'
import { FieldsetSelect, Option as FieldsetSelectOption } from '@/modules/settings/components/FieldsetSelect'

interface AddServerCompanyModalProps {
  isOpen: boolean
  close: () => void
  serverId: number
  serverName: string
  tenantCompanyId: string
  defaultUseExistingDatabase?: boolean
}

export const AddServerCompanyModal: React.FC<AddServerCompanyModalProps> = ({
  isOpen,
  close,
  serverId,
  serverName,
  tenantCompanyId,
  defaultUseExistingDatabase = false,
}) => {
  const { t } = useLocalize()
  const loading = useLoadingStore(selectLoading)
  const startLoading = useLoadingStore((s) => s.startLoading)

  const createMutation = useMutationCreateCompanyOnServer(serverId)
  const { data: wpfCompanies = [], isLoading: isLoadingWpf } = useQueryWpfCompaniesOnServer(
    isOpen ? serverId : null,
  )

  const [useExistingDatabase, setUseExistingDatabase] = React.useState(defaultUseExistingDatabase)
  const [companyName, setCompanyName] = React.useState('')
  const [uniqueIdentifier, setUniqueIdentifier] = React.useState('')
  const [nip, setNip] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [selectedWpfIdentifier, setSelectedWpfIdentifier] = React.useState<string>('')
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (isOpen) {
      setUseExistingDatabase(defaultUseExistingDatabase)
      setCompanyName('')
      setUniqueIdentifier('')
      setNip('')
      setEmail('')
      setSelectedWpfIdentifier('')
      setError(null)
    }
  }, [isOpen, defaultUseExistingDatabase])

  const availableWpfCompanies = React.useMemo(
    () => wpfCompanies.filter((c) => !c.inUse),
    [wpfCompanies],
  )

  const selectedWpfCompany: WpfCompanyModel | undefined = React.useMemo(
    () => availableWpfCompanies.find((c) => c.uniqueIdentifier === selectedWpfIdentifier),
    [availableWpfCompanies, selectedWpfIdentifier],
  )

  React.useEffect(() => {
    if (useExistingDatabase && selectedWpfCompany) {
      setCompanyName((cur) => cur || selectedWpfCompany.name)
      setUniqueIdentifier((cur) => cur || selectedWpfCompany.uniqueIdentifier)
    }
  }, [useExistingDatabase, selectedWpfCompany])

  const canSubmit =
    companyName.trim().length > 0 &&
    uniqueIdentifier.trim().length > 0 &&
    (!useExistingDatabase || selectedWpfCompany != null)

  const handleConfirm = async () => {
    setError(null)
    const stopLoading = startLoading()
    try {
      await createMutation.mutateAsync({
        tenantId: tenantCompanyId,
        serverId,
        companyName: companyName.trim(),
        uniqueIdentifier: uniqueIdentifier.trim(),
        nip: nip.trim() || undefined,
        email: email.trim() || undefined,
        wpfCompanies: useExistingDatabase && selectedWpfCompany ? [selectedWpfCompany] : undefined,
      })
      close()
    } catch (err: any) {
      const code = err?.response?.data?.code
      const message =
        err?.response?.data?.message ||
        err?.message ||
        t('server-databases.add-company-error')
      setError(code ? `${code}: ${message}` : message)
    } finally {
      stopLoading()
    }
  }

  if (!isOpen) return null

  return (
    <ModalLayout>
      <div className="tw-relative tw-flex tw-max-h-[90vh] tw-w-full tw-max-w-[560px] tw-flex-col tw-rounded-lg tw-bg-white">
        {loading && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-[200] tw-grid tw-select-none tw-place-items-center tw-bg-black/40">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        )}

        <div className="tw-border-b tw-border-base-300 tw-px-4 tw-py-4">
          <div className="tw-flex tw-items-center tw-justify-between">
            <div>
              <p className="tw-text-xl tw-font-semibold tw-text-content-base">
                {t('server-databases.add-company')}
              </p>
              <p className="tw-text-xs tw-text-gray-500">{serverName}</p>
            </div>
            <FaIcon
              icon={faClose}
              className="tw-h-5 tw-w-5 tw-cursor-pointer"
              onClick={close}
            />
          </div>
        </div>

        <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-6">
          <div className="tw-space-y-4">
            <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm tw-text-content-base">
              <input
                type="checkbox"
                checked={useExistingDatabase}
                onChange={() => setUseExistingDatabase((v) => !v)}
                className="tw-h-4 tw-w-4 tw-cursor-pointer tw-accent-primary"
              />
              {t('server-databases.use-existing-database')}
            </label>

            {useExistingDatabase && (
              <FieldsetSelect
                label={t('server-databases.existing-database')}
                placeholder={
                  isLoadingWpf ? t('server-databases.loading') : t('server-databases.pick-database')
                }
                disabled={isLoadingWpf}
                background
                value={selectedWpfIdentifier}
                handleChange={(value) => setSelectedWpfIdentifier(value)}
                options={availableWpfCompanies.map<FieldsetSelectOption>((c) => ({
                  key: c.uniqueIdentifier,
                  value: c.uniqueIdentifier,
                  label: `${c.name} (${c.uniqueIdentifier})`,
                }))}
              />
            )}

            <div>
              <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-content-base">
                {t('administrationPanel-companies.company-name')}
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="tw-w-full tw-rounded-lg tw-border tw-border-base-300 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base focus:tw-border-primary focus:tw-outline-none"
              />
            </div>

            <div>
              <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-content-base">
                {t('administrationPanel-companies.unique-identifier')}
              </label>
              <input
                type="text"
                value={uniqueIdentifier}
                onChange={(e) => setUniqueIdentifier(e.target.value)}
                className="tw-w-full tw-rounded-lg tw-border tw-border-base-300 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base focus:tw-border-primary focus:tw-outline-none"
              />
            </div>

            <div className="tw-grid tw-grid-cols-2 tw-gap-3">
              <div>
                <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-content-base">
                  {t('administrationPanel-companies.nip')}
                </label>
                <input
                  type="text"
                  value={nip}
                  onChange={(e) => setNip(e.target.value)}
                  className="tw-w-full tw-rounded-lg tw-border tw-border-base-300 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base focus:tw-border-primary focus:tw-outline-none"
                />
              </div>
              <div>
                <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-content-base">
                  {t('administrationPanel-companies.email')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="tw-w-full tw-rounded-lg tw-border tw-border-base-300 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base focus:tw-border-primary focus:tw-outline-none"
                />
              </div>
            </div>

            {error && (
              <div className="tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-p-4">
                <p className="tw-text-sm tw-text-red-800">{error}</p>
              </div>
            )}
          </div>
        </div>

        <div className="tw-border-t tw-border-base-300 tw-px-4 tw-py-4">
          <div className="tw-flex tw-justify-end tw-gap-3">
            <button
              type="button"
              onClick={close}
              disabled={loading}
              className="tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-100 disabled:tw-opacity-50"
            >
              {t('administrationPanel-companies.duplicate-database-cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading || !canSubmit}
              className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90 disabled:tw-opacity-50"
            >
              {t('server-databases.add-company-confirm')}
            </button>
          </div>
        </div>
      </div>
    </ModalLayout>
  )
}
